import React, { useEffect, useState } from 'react';
import { Box, Button, TextField, IconButton, MenuItem } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useCountryParam, buildUrlWithCountry } from '../../common/util/useCountryParam';
import { useFormik } from 'formik';
import * as yup from 'yup';
import TextEditor from '../../common/TextEditor';
import { createBlog, updateBlog, getBlog } from './Blog.service';
import { Grid2 as Grid } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { Delete as DeleteIcon } from '@mui/icons-material';

const validationSchema = yup.object({
  title: yup.string().required('Title is required'),
  excerpt: yup.string().required('Excerpt is required'),
  content: yup.string().required('Content is required'),
  image: yup.mixed().required('Image is required'),
});

const AddBlog: React.FC = () => {
  const navigate = useNavigate();
  const country = useCountryParam();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      title: '',
      excerpt: '',
      content: '',
      image: '',
      category: 'Property Tips',
      tags: '',
      featured: false,
      readTime: 5,
      status: 'Published'
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const formData = new FormData();

        // Append all fields
        formData.append('title', values.title);
        formData.append('excerpt', values.excerpt);
        formData.append('content', values.content);
        formData.append('category', values.category);
        formData.append('readTime', String(values.readTime));
        formData.append('status', values.status);
        formData.append('featured', String(values.featured));

        const tagsArray = typeof values.tags === 'string' ? values.tags.split(',').map((t: string) => t.trim()).filter(Boolean) : values.tags;
        formData.append('tags', JSON.stringify(tagsArray));

        if ((values.image as any) instanceof File) {
          formData.append('image', values.image as any);
        } else {
          formData.append('existingImage', values.image as any);
        }

        if (id) {
          await updateBlog(id, formData);
        } else {
          await createBlog(formData);
        }
        navigate(buildUrlWithCountry('/blogs', country));
      } catch (err) {
        console.error(err);
        alert('Save failed');
      } finally {
        setLoading(false);
      }
    }
  });

  useEffect(() => {
    if (id) {
      (async () => {
        try {
          const res: any = await getBlog(id as string);
          if (res) {
            formik.setValues({
              title: res.title || '',
              excerpt: res.excerpt || '',
              content: res.content || '',
              image: res.image || '',
              category: res.category || 'Property Tips',
              tags: Array.isArray(res.tags) ? res.tags.join(',') : (res.tags || ''),
              featured: !!res.featured,
              readTime: res.readTime || 5,
              status: res.status || 'Published'
            });
          }
        } catch (err) {
          console.error(err);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      formik.setFieldValue('image', acceptedFiles[0]);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] },
    multiple: false
  });

  return (
    <Box sx={{ paddingY: 6, width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
        <h2>{id ? 'Edit Blog' : 'Add Blog'}</h2>
      </Box>

      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <label className="form-label">Title</label>
            <TextField fullWidth margin="dense" placeholder="Title" name="title" value={formik.values.title} onChange={formik.handleChange} error={Boolean(formik.touched.title && formik.errors.title)} helperText={formik.touched.title && formik.errors.title} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <label className="form-label">Excerpt</label>
            <TextField fullWidth margin="dense" placeholder="Excerpt" name="excerpt" value={formik.values.excerpt} onChange={formik.handleChange} error={Boolean(formik.touched.excerpt && formik.errors.excerpt)} helperText={formik.touched.excerpt && formik.errors.excerpt} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <label className="form-label">Category</label>
            <TextField select fullWidth margin="dense" name="category" value={formik.values.category} onChange={formik.handleChange}>
              {['Property Tips', 'Market Insights', 'Company News', 'Investment Guide', 'Area Guide'].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <label className="form-label">Read Time (min)</label>
            <TextField fullWidth margin="dense" type="number" placeholder="Read Time" name="readTime" value={formik.values.readTime} onChange={formik.handleChange} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <label className="form-label">Status</label>
            <TextField select fullWidth margin="dense" name="status" value={formik.values.status} onChange={formik.handleChange}>
              {['Draft', 'Published', 'Archived'].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <label className="form-label">Tags (comma separated)</label>
            <TextField fullWidth margin="dense" placeholder="Tags" name="tags" value={formik.values.tags} onChange={formik.handleChange} />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <label className="form-label">Image</label>
            <Box
              {...getRootProps()}
              sx={{
                border: '2px dashed #ccc',
                padding: 3,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: isDragActive ? '#f0f0f0' : 'inherit',
                borderRadius: 1,
                mb: 2,
                mt: 1
              }}
            >
              <input {...getInputProps()} />
              <p>Drag & drop image here, or click to select file</p>
            </Box>
            {formik.values.image && (
              <Box sx={{ mt: 2, position: 'relative', width: 'fit-content' }}>
                <img
                  src={typeof formik.values.image === 'string' ? formik.values.image : URL.createObjectURL(formik.values.image)}
                  alt="Preview"
                  style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '4px', objectFit: 'cover' }}
                />
                <IconButton
                  sx={{ position: 'absolute', top: -10, right: -10, bgcolor: 'background.paper', boxShadow: 1 }}
                  size="small"
                  onClick={() => formik.setFieldValue('image', '')}
                >
                  <DeleteIcon color="error" fontSize="small" />
                </IconButton>
              </Box>
            )}
            {formik.touched.image && formik.errors.image && (
              <p style={{ color: '#d32f2f', fontSize: '0.75rem', margin: '3px 14px 0' }}>{formik.errors.image as string}</p>
            )}
          </Grid>

          <Grid size={{ xs: 12 }}>
            <label className="form-label">Content</label>
            <Box sx={{ marginY: 2 }}>
              <TextEditor value={formik.values.content} setFieldWithEditor={(html: string) => formik.setFieldValue('content', html)} />
            </Box>
            {formik.touched.content && formik.errors.content && (
              <p style={{ color: '#d32f2f', fontSize: '0.75rem', margin: '3px 14px 0' }}>{formik.errors.content as string}</p>
            )}
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, marginTop: 2 }}>
              <Button variant="contained" color="primary" type="submit" disabled={loading} size="small" sx={{
                backgroundColor: "#10374A",
                color: "#FFFFFF",
                "&:hover": { backgroundColor: "#08293D" }, height: '40px'
              }}>{id ? 'Save' : 'Create'}</Button>
              <Button variant="outlined" size="small" onClick={() => navigate(buildUrlWithCountry('/blogs', country))}>Cancel</Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default AddBlog;

